/** * Shared settings layout with a content area and a persistent action bar. * * This template
standardizes how configuration forms expose save/cancel * actions across the admin center. Pages can
either rely on the default * buttons or replace the action area through slots when they need
additional * controls. */
<script lang="ts" setup>
import { onBeforeUnmount, watchEffect } from 'vue'
import { useRouter } from 'vue-router'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { tryUsePageHeader } from '@/composables/usePageHeader'

const props = withDefaults(
  defineProps<{
    /** Label used for the default cancel button. */
    cancelLabel?: string
    /** Label used for the default save button. */
    saveLabel?: string
    /** Font Awesome icon class used for the cancel action. */
    cancelIcon?: string
    /** Font Awesome icon class used for the save action. */
    saveIcon?: string
    /** Displays a loading state on the save action. */
    loading?: boolean
    /** Disables the save action. */
    saveDisabled?: boolean
    /** Disables the cancel action. */
    cancelDisabled?: boolean
    /** Controls whether the default cancel button is rendered. */
    showCancel?: boolean
    /** Controls whether the default save button is rendered. */
    showSave?: boolean
    /** ISO date string shown as "Zuletzt gespeichert" in the action bar. */
    lastUpdated?: string
    /** Routes save/cancel to the shell's main content header instead of the bottom bar. */
    useHeaderActions?: boolean
    /** When false, cancel only emits the event and does not navigate back. */
    cancelNavigatesBack?: boolean
    /**
     * Marks the form as dirty. When `useHeaderActions` is enabled, this locks the Back button
     * and enables the reset button in the main content header.
     */
    dirty?: boolean
  }>(),
  {
    cancelLabel: 'Cancel',
    saveLabel: 'Save',
    cancelIcon: 'fa fa-xmark',
    saveIcon: 'fa fa-save',
    loading: false,
    saveDisabled: false,
    cancelDisabled: false,
    showCancel: true,
    showSave: true,
    useHeaderActions: false,
    cancelNavigatesBack: true,
    dirty: false
  }
)

const emit = defineEmits<{
  (e: 'save'): void
  (e: 'cancel'): void
}>()

const router = useRouter()

function handleCancel() {
  emit('cancel')
  if (props.cancelNavigatesBack && window.history.length > 1) {
    router.back()
  }
}

function handleSave() {
  emit('save')
}

const pageHeader = props.useHeaderActions ? tryUsePageHeader() : null
const usesHeaderActions = props.useHeaderActions && !!pageHeader

if (usesHeaderActions && pageHeader) {
  watchEffect(() => {
    pageHeader.setDirty(props.dirty)

    pageHeader.setSave(
      props.showSave
        ? {
            label: props.saveLabel,
            icon: props.saveIcon,
            handler: handleSave,
            loading: props.loading,
            disabled: props.saveDisabled
          }
        : null
    )

    pageHeader.setReset(
      props.showCancel
        ? {
            label: props.cancelLabel,
            icon: props.cancelIcon,
            handler: handleCancel,
            disabled: props.cancelDisabled || props.loading
          }
        : null
    )
  })

  onBeforeUnmount(() => {
    pageHeader.setSave(null)
    pageHeader.setReset(null)
    pageHeader.setDirty(false)
  })
}
</script>

<template>
  <div class="settings-page">
    <div v-if="$slots['top-content']" class="settings-top">
      <slot name="top-content" />
    </div>

    <div class="settings-content">
      <!-- Main form content rendered by the consuming settings page. -->
      <slot name="settings-content" />
    </div>

    <div v-if="!usesHeaderActions" class="settings-actions">
      <div class="actions-left">
        <!-- Optional left-aligned actions such as secondary toggles. -->
        <slot name="actions-left" />
        <span v-if="lastUpdated" class="last-updated">
          <FontAwesomeIcon icon="fa-regular fa-clock" />
          {{
            new Date(lastUpdated).toLocaleString('de-DE', {
              dateStyle: 'short',
              timeStyle: 'short'
            })
          }}
        </span>
      </div>
      <div class="actions-right">
        <!-- Override this slot to replace the default save/cancel buttons. -->
        <slot name="actions-right">
          <Button
            v-if="showCancel"
            :label="cancelLabel"
            :icon="cancelIcon"
            severity="secondary"
            outlined
            :disabled="cancelDisabled || loading"
            @click="handleCancel"
          />
          <Button
            v-if="showSave"
            :label="saveLabel"
            :icon="saveIcon"
            :loading="loading"
            :disabled="saveDisabled"
            @click="handleSave"
          />
        </slot>
      </div>
    </div>
  </div>
</template>

<style scoped>
.settings-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}

.settings-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.settings-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-content :deep(.p-datatable) {
  border: 1px solid #e8e6e0;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
}

.settings-content :deep(.p-datatable .p-datatable-thead > tr > th) {
  background: #f7f7f5;
  color: #3a3c42;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  border-bottom: 1px solid #ece9e3;
}

.settings-content :deep(.p-datatable .p-datatable-tbody > tr > td) {
  border-bottom: 1px solid #f2f0ea;
  font-size: 0.88rem;
}

.settings-content :deep(.p-datatable .p-datatable-tbody > tr:last-child > td) {
  border-bottom: none;
}

.settings-content :deep(.p-datatable .p-datatable-tbody > tr:hover) {
  background: #fafaf8;
}

.settings-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #f0efea;
  margin-top: 0.5rem;
}

.actions-left,
.actions-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.actions-right {
  justify-content: flex-end;
  margin-left: auto;
}

.last-updated {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: var(--text-color-secondary, #9ca3af);
}
</style>
